import useToneOfVoice from "@/hooks/useToneOfVoice";
import ToneOfVoice from "@/models/tone-of-voice/toneOfVoice";
import {
  showErrorToast,
  showSuccessToast,
} from "@/components/custom-toasts/toasts";
import { logger } from "@/lib/logger";

export const useToneOfVoiceCrud = () => {
  const { useQueryPagination, create, update, remove, invalidateQueries } =
    useToneOfVoice();

  const fetchedToneOfVoice = useQueryPagination({
    pageNumber: 1,
    pageSize: 100,
    search: "",
    sortAscending: true,
    sortBy: "",
    filters: [],
  });

  // Enhanced CRUD operations
  const handleDelete = async (toneOfVoiceId: string) => {
    try {
      await remove.mutateAsync(toneOfVoiceId);
      invalidateQueries();
      showSuccessToast("Tone of voice blev slettet", "Tone of voice");
    } catch (error) {
      logger.error("Failed to delete tone of voice", {
        operation: "deleteToneOfVoice",
        error,
        toneOfVoiceId,
      });
      showErrorToast(
        "Der skete en fejl ved sletning - prøv igen.",
        "Tone of voice"
      );
    }
  };

  const handleUpdate = async (values: Partial<ToneOfVoice>) => {
    try {
      const updatedToneOfVoice = (await update.mutateAsync({
        ...values,
        id: values.id,
      })) as ToneOfVoice;
      if (updatedToneOfVoice) {
        invalidateQueries();

        showSuccessToast("Tone of voice blev opdateret", "Tone of voice");
      }
    } catch (error) {
      showErrorToast(
        "Der skete en fejl ved opdatering - prøv igen.",
        "Tone of voice"
      );
      throw error;
    }
  };

  const handleCreate = async (values: Omit<ToneOfVoice, "id">) => {
    try {
      const newToneOfVoice = (await create.mutateAsync(values)) as ToneOfVoice;
      if (newToneOfVoice) {
        invalidateQueries();
        showSuccessToast("Tone of voice blev oprettet", "Tone of voice");
      }
    } catch (error) {
      showErrorToast(
        "Der skete en fejl ved oprettelse - prøv igen.",
        "Tone of voice"
      );
      throw error;
    }
  };

  return {
    // State
    fetchedToneOfVoice,
    // Enhanced CRUD operations
    handleDelete,
    handleUpdate,
    handleCreate,
    // Form helpers
    isCreating: create.isPending,
    isUpdating: update.isPending,
    isDeleting: remove.isPending,
    isLoading: fetchedToneOfVoice.isPending,
  };
};

export type ToneOfVoiceCrudHook = ReturnType<typeof useToneOfVoiceCrud>;
